// NOTICE: KAPTION-INTEGRATION
// Sidebar filter/section visibility gates (Famiq ask, Jul 2026).
//
// Maps the stable `name` of each governable conversation-sidebar entry to its
// feature-permission registry key (config/kaption_feature_registry.yml,
// area: navigation). Shared by KaptionSidebar.vue and KaptionSidebarV2.vue so
// the duplicated menu builders can't drift on gating behavior.

export const SIDEBAR_ENTRY_PERMISSION_KEYS = {
  Unattended: 'sidebar_filter_unattended',
  Mine: 'sidebar_filter_mine',
  Unassigned: 'sidebar_filter_unassigned',
  Unread: 'sidebar_filter_unread',
  Mentions: 'sidebar_filter_mentions',
  Folders: 'sidebar_section_folders',
  Teams: 'sidebar_section_teams',
  Channels: 'sidebar_section_channels',
  Labels: 'sidebar_section_labels',
  // Covers both shapes getKaptionAssistantItems() builds (subgroup or leaf)
  KaptionAssistants: 'sidebar_section_assistants',
  // Per-person conversation views (agent_conversations route); offered by
  // the chat-list view switcher.
  People: 'sidebar_section_agents',
};

// Chat-list assignee tabs governed by the same keys as their sidebar twins,
// so hiding a filter hides it everywhere at once.
export const ASSIGNEE_TAB_PERMISSION_KEYS = {
  me: 'sidebar_filter_mine',
  unassigned: 'sidebar_filter_unassigned',
  unread: 'sidebar_filter_unread',
};

/**
 * Filter a built sidebar children array by the account's feature-permission
 * scopes. Entries without a registered key (All, Favorites, assistants, …)
 * always pass. The Channels section additionally auto-hides when the user has
 * at most one inbox — the filter is redundant then (the inbox list the user
 * sees is already scoped server-side to their memberships).
 *
 * @param {Array<Object>} children built menu child objects (stable `name`)
 * @param {Function} canUseFeature from useFeaturePermissions()
 * @param {Object} [opts]
 * @param {number} [opts.inboxCount] the current user's visible inbox count
 * @returns {Array<Object>}
 */
export function applySidebarFilterGates(children, canUseFeature, opts = {}) {
  return children.filter(child => {
    if (
      child.name === 'Channels' &&
      typeof opts.inboxCount === 'number' &&
      opts.inboxCount <= 1
    ) {
      return false;
    }

    const permissionKey = SIDEBAR_ENTRY_PERMISSION_KEYS[child.name];
    if (!permissionKey) return true;
    return canUseFeature(permissionKey);
  });
}

/**
 * Same contract for the chat-list assignee tabs ({key: 'me'|'unassigned'|...}).
 */
export function applyAssigneeTabGates(tabs, canUseFeature) {
  return tabs.filter(tab => {
    const permissionKey = ASSIGNEE_TAB_PERMISSION_KEYS[tab.key];
    if (!permissionKey) return true;
    return canUseFeature(permissionKey);
  });
}
